import React, { useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { Button, Stack } from 'react-bootstrap';
import DataTable, { TableColumn } from 'react-data-table-component';
import {
  routeUserDashboardInsurancePolicyClaim,
  routeUserDashboardInsurancePolicyUserDetailsPage,
} from '@components/UserDashboardRoute';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye } from '@fortawesome/free-solid-svg-icons';
import { tGender } from '@models/User';
import { InsurancePolicyUser } from '@models/InsurancePolicyUser';
import { formatDate } from '@utils/datetime';
import { insurancePolicyUsersFetch, InsurancePolicyUsersFetchPayload } from './action';
import { selectSearchInsurancePolicyUserPageReducer } from './selector';

const customStyles = {
  headCells: {
    style: {
      fontWeight: 'bold',
    },
  },
};

export const useColumns = () => {
  const { t } = useTranslation();

  return useMemo(
    () =>
      [
        {
          cell: ({ id, insurancePolicy }) => (
            <Stack direction="horizontal" gap={1}>
              <Button
                variant="outline-info"
                size="sm"
                as={Link}
                to={routeUserDashboardInsurancePolicyUserDetailsPage({ insurancePolicyUserId: id })}
              >
                <FontAwesomeIcon icon={faEye} />
              </Button>
              <Button
                as={Link}
                to={routeUserDashboardInsurancePolicyClaim({
                  insurancePolicyId: insurancePolicy.id,
                  insurancePolicyUserId: id,
                })}
                variant="outline-primary"
                size="sm"
              >
                {t('common:claim')}
              </Button>
            </Stack>
          ),
          button: 'true',
          center: 'true',
        },
        {
          name: t('common:memberId'),
          selector: (row) => row.memberId,
          width: '150px',
        },
        {
          name: t('user.name'),
          selector: ({ user }) => user.name,
          width: '150px',
        },
        {
          name: t('common:policyNumber'),
          selector: (row) => row.insurancePolicy?.policyNumber,
          width: '180px',
        },
        {
          name: t('user.dob'),
          selector: ({ user }) => formatDate(user.dob),
          width: '150px',
        },
        {
          name: t('user.gender'),
          selector: ({ user }) => tGender(user.gender),
        },
        {
          name: 'Relationship',
          selector: ({ familyHead }) => (familyHead ? 'Dependent' : 'Family'),
        },
        {
          name: 'Family head',
          selector: ({ familyHead }) => familyHead?.name,
          width: '120px',
        },
        {
          name: t('common:effectiveDate'),
          selector: (row) => row.effectiveDate,
          format: (row) => formatDate(row.effectiveDate),
          width: '150px',
        },
        {
          name: t('common:expirationDate'),
          selector: (row) => row.expirationDate,
          format: (row) => formatDate(row.expirationDate),
          width: '150px',
        },
        {
          name: t('common:plan'),
          selector: ({ benefitTemplate }) => benefitTemplate.name,
          width: '120px',
        },
      ] as TableColumn<InsurancePolicyUser>[],
    [],
  );
};

const InsurancePolicyUserList = () => {
  const dispatch = useDispatch();
  const columns = useColumns();
  const [searchParams, setSearchParams] = useSearchParams();
  const { insurancePolicyUsers, pagination } = useSelector(selectSearchInsurancePolicyUserPageReducer);

  const handlePageChange = (page: number) => {
    setSearchParams({ ...Object.fromEntries(searchParams), page: page.toString() });
  };

  const handlePerRowsChange = (newPerPage: number, page: number) => {
    setSearchParams({
      ...Object.fromEntries(searchParams),
      perPage: newPerPage.toString(),
      page: page.toString(),
    });
  };

  useEffect(() => {
    const payload: InsurancePolicyUsersFetchPayload = Object.fromEntries(searchParams);
    dispatch(insurancePolicyUsersFetch(payload));
  }, [searchParams]);

  return (
    <DataTable
      columns={columns}
      data={insurancePolicyUsers ?? []}
      customStyles={customStyles}
      fixedHeader
      pagination
      paginationServer
      paginationTotalRows={pagination?.total ?? 0}
      paginationPerPage={pagination?.perPage ?? 20}
      onChangePage={handlePageChange}
      onChangeRowsPerPage={handlePerRowsChange}
    />
  );
};

export { InsurancePolicyUserList };
